import { importLibrary, setOptions } from '@googlemaps/js-api-loader'

type PlaceAutocompleteElementClass = typeof google.maps.places.PlaceAutocompleteElement

let placeAutocompleteElementPromise: Promise<PlaceAutocompleteElementClass> | null = null

/**
 * Load the Google Maps Places library once and return PlaceAutocompleteElement.
 * @param language - Optional ISO 639-1 language for Places labels
 */
export const loadPlaceAutocompleteElement = async (
  language?: string,
): Promise<PlaceAutocompleteElementClass> => {
  const apiKey = process.env.GOOGLE_MAPS_API_KEY
  if (!apiKey) {
    throw new Error('GOOGLE_MAPS_API_KEY is not configured')
  }

  if (!placeAutocompleteElementPromise) {
    setOptions({
      key: apiKey,
      v: 'weekly',
      language: language?.toLowerCase(),
    })

    placeAutocompleteElementPromise = importLibrary('places').then(
      library => library.PlaceAutocompleteElement as PlaceAutocompleteElementClass,
    )
  }

  return placeAutocompleteElementPromise
}

type MappedAddress = {
  streetNumber?: string
  /** Street number + name only (API payload and Google Sheet "Adresse de livraison"). */
  streetLine: string
  /** Full line shown in the autocomplete widget after selection. */
  formatted: string
  zip: string
  city: string
  country: string
  isComplete: boolean
}

/**
 * Read a component longText from Google address components (New Places API).
 */
const getComponentLong = (
  components: google.maps.places.AddressComponent[],
  ...types: string[]
): string | undefined =>
  components
    .find(component => types.every(type => component.types.includes(type)))
    ?.longText?.trim()

/**
 * Read a component shortText from Google address components (New Places API).
 */
const getComponentShort = (
  components: google.maps.places.AddressComponent[],
  type: string,
): string | undefined =>
  components.find(component => component.types.includes(type))?.shortText?.trim()

/**
 * Map a Google Place (after fetchFields) to delivery form values.
 */
export const mapGooglePlaceToAddress = (place: google.maps.places.Place): MappedAddress | null => {
  const components = place.addressComponents ?? []

  const streetNumber =
    getComponentLong(components, 'street_number') || getComponentLong(components, 'premise')

  const route =
    getComponentLong(components, 'route') ||
    getComponentLong(components, 'street_address') ||
    getComponentLong(components, 'neighborhood')

  const city =
    getComponentLong(components, 'locality') ||
    getComponentLong(components, 'postal_town') ||
    getComponentLong(components, 'administrative_area_level_2') ||
    getComponentLong(components, 'sublocality') ||
    getComponentLong(components, 'sublocality_level_1')
  const postcode = getComponentLong(components, 'postal_code') || 'N/A'
  const country = getComponentShort(components, 'country')?.toUpperCase()

  if (route && city && postcode && country) {
    const streetLine = streetNumber ? `${streetNumber} ${route}` : route
    return {
      streetLine,
      formatted: `${streetLine}, ${postcode} ${city}`,
      zip: postcode,
      city,
      country,
      isComplete: Boolean(streetNumber),
      streetNumber,
    }
  }

  const formattedAddress = place.formattedAddress?.trim()
  if (!formattedAddress) {
    return null
  }

  // Fallback when Google returns formattedAddress without granular components.
  const postcodeMatch = formattedAddress.match(/\b(\d{4,6})\b/)
  const zip = postcodeMatch?.[1] ?? ''
  const streetLine =
    streetNumber && route
      ? `${streetNumber} ${route}`
      : formattedAddress.split(',')[0]?.trim() || formattedAddress
  const isComplete = Boolean(streetNumber) || /^\d+\s/.test(streetLine)

  return {
    streetLine,
    formatted: formattedAddress,
    zip,
    city: city ?? '',
    country: country ?? '',
    isComplete,
    streetNumber: streetNumber ?? '',
  }
}
